import { Alert, Box, TextField } from "@mui/material";
import { EditorComponent, ThemeProvider } from "@remirror/react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useHtmlEditorContext } from "./useHtmlEditorContext";
import ErrorBoundary from "../../contexts/ErrorBoundary";
import { logError } from "../../services/logging";
import { devWarn, devLog } from "../../services/util";

// Create a memoized version of RootEditorComponent to ensure it's only created once per editorId
const RootEditorComponent = ({ editorId }) => {
  useEffect(() => {
    devLog(`remirror-test: 🟢 RootEditorComponent MOUNTED: ${editorId}`);
    return () => {
      devLog(`remirror-test: 🔴 RootEditorComponent UNMOUNTED: ${editorId}`);
    };
  }, [editorId]);

  return (
    <ErrorBoundary
      errorContent={<Alert severity="error">Editor error</Alert>}
      onError={(error) => {
        logError(error, "RootEditorComponent: " + editorId);
      }}
    >
      <EditorComponent />
    </ErrorBoundary>
  );
};

export type HtmlEditorBoxProps = {
  onSourceEdit?: (html: string) => void;
  editorId: string;
  compact?: boolean;
  embedded?: boolean;
  maxHeight?: number;
  onError?: () => void;
};

export const HtmlEditorBox = ({
  onSourceEdit,
  editorId,
  compact,
  embedded,
  maxHeight,
  onError,
}: HtmlEditorBoxProps) => {
  const { isSourceEdit, editedHtml, darkBkg, setEditedHtml, fullScreen } =
    useHtmlEditorContext();

  const [, setEditorWidth] = useState(0);
  const [hasError, setHasError] = useState(false);
  const [hasContext, setHasContext] = useState(false);

  const ref = useRef<HTMLDivElement>(null);

  // Log HtmlEditorBox lifecycle
  useEffect(() => {
    devLog(
      `remirror-test: 📦 HtmlEditorBox MOUNTED: ${editorId}, isSourceEdit: ${isSourceEdit}`
    );
    return () => {
      devLog(`remirror-test: 📦 HtmlEditorBox UNMOUNTED: ${editorId}`);
    };
  }, [editorId, isSourceEdit]);

  // Memoize the RootEditorComponent to ensure it's only created once per editorId
  const memoizedEditor = useMemo(() => {
    return <RootEditorComponent editorId={editorId} />;
  }, [editorId]);

  // Check if we have a valid context
  useEffect(() => {
    // Consider context valid if we have editedHtml (even if it's an empty string)
    // or if we're in source edit mode
    const contextValid =
      (editedHtml !== undefined && editedHtml !== null) || isSourceEdit;
    setHasContext(contextValid);

    // Only log the warning once per editor instance
    if (embedded && !contextValid) {
      devWarn(`HtmlEditorBox (${editorId}) is embedded but has no context`);
    }
  }, [embedded, editedHtml, isSourceEdit, editorId]);

  useLayoutEffect(() => {
    if (!isSourceEdit) {
      setEditorWidth(ref.current?.offsetWidth ?? 0);
    }
  }, [isSourceEdit, ref.current?.offsetWidth, setEditorWidth]);

  // Calculate editor height based on mode
  const getEditorHeight = () => {
    if (maxHeight) return `${maxHeight}px`;
    if (fullScreen) return "calc(100vh - 250px)";
    if (compact && !embedded) return "120px"; // Smaller height in compact mode
    return "auto";
  };

  // Reset error state when editedHtml changes
  useEffect(() => {
    setHasError(false);
  }, [editedHtml]);

  // Don't render anything if we don't have a valid context
  if (embedded && !hasContext) {
    return null;
  }

  return (
    <Box
      ref={ref}
      sx={{
        "& .remirror-editor-wrapper": {
          pt: 0,
          display: isSourceEdit ? "none" : "block",
          "& > div": {
            backgroundColor: darkBkg && !isSourceEdit ? "#333" : null,
          },
          "& .remirror-editor": {
            height: getEditorHeight(),
            maxHeight: maxHeight ?? "none",
            minHeight: maxHeight ?? "none",
          },
        },
      }}
    >
      {!isSourceEdit && !hasError && (
        <ErrorBoundary
          errorContent={<Alert severity="error">Editor error</Alert>}
          onError={(error) => {
            logError(error, "HtmlEditorBox: " + editorId);
            setHasError(true);
            onError?.();
          }}
        >
          <ThemeProvider>{memoizedEditor}</ThemeProvider>
        </ErrorBoundary>
      )}

      {isSourceEdit && (
        <Box>
          <TextField
            sx={{
              mt: 1,
              width: "100%",
              height: fullScreen
                ? "calc(100vh - 250px)"
                : compact
                ? "120px"
                : "400px",
              "& .MuiInputBase-root": {
                bgcolor: "text.primary",
                color: "background.paper",
                p: 0,
                height: "100%",
                minHeight: compact ? "50px" : "100px",
              },
              "& textarea": {
                height: "90% !important",
                overflowY: "auto !important",
                p: 2,
              },
            }}
            fullWidth
            multiline
            value={editedHtml ?? ""}
            onChange={(e) => {
              setEditedHtml(e.target.value);
              onSourceEdit?.(e.target.value);
            }}
          />
        </Box>
      )}
    </Box>
  );
};
